import moment from 'moment';

import { HISTORY_RANGE, HISTORY_TYPE } from '@/constants/history';
import {
  AccountBalanceHistory,
  FundingHistory,
  LiquidityHistory,
  OrderHistory,
  TradeHistory,
  TransferHistory,
} from '@/features/graph/types';

import { TokenInfoFromApi, TokenInfoMap } from '@/types/token';

export function getStartEndTimeByRangeType(rangeType: HISTORY_RANGE): { startTime: number; endTime: number } {
  const utcNow = moment.utc();
  const endTime = utcNow.unix();
  if (rangeType === HISTORY_RANGE.D_1) {
    const startTime = utcNow.add(-1, 'd').unix();
    return { startTime, endTime };
  } else if (rangeType === HISTORY_RANGE.D_7) {
    const startTime = utcNow.add(-7, 'd').unix();
    return { startTime, endTime };
  } else if (rangeType === HISTORY_RANGE.M_1) {
    const startTime = utcNow.add(-1, 'month').unix();
    return { startTime, endTime };
  } else if (rangeType === HISTORY_RANGE.M_3) {
    const startTime = utcNow.add(-3, 'month').unix();
    return { startTime, endTime };
  }

  const startTime = 0;
  return { startTime, endTime };
}

type TtransformHistoryDataToCsvData =
  | {
      type: HISTORY_TYPE.TRADE;
      data: TradeHistory[];
    }
  | {
      type: HISTORY_TYPE.ORDERS;
      data: OrderHistory[];
    }
  | {
      type: HISTORY_TYPE.LIQUIDITY;
      data: LiquidityHistory[];
    }
  | {
      type: HISTORY_TYPE.FUNDING;
      data: FundingHistory[];
    }
  | {
      type: HISTORY_TYPE.TRANSFERS;
      data: TransferHistory[];
    }
  | {
      type: HISTORY_TYPE.ACCOUNT;
      data: AccountBalanceHistory[];
      marginTokenMap?: TokenInfoMap | undefined;
    };

export const getSymbolAndTimeAndPair = (item: {
  timestamp: number;
  symbol: string;
  baseToken: TokenInfoFromApi;
  quoteToken: TokenInfoFromApi;
}) => {
  return {
    symbolAndTime: {
      time: new Date(item.timestamp * 1000).toLocaleString('en-GB'),
      'pair symbol': item?.symbol,
      'base symbol': item?.baseToken.symbol,
      'quote symbol': item?.quoteToken.symbol,
    },
  };
};
export const transformHistoryDataToCsvData = (props: TtransformHistoryDataToCsvData): object[] => {
  if (props.type === HISTORY_TYPE.TRADE) {
    const { data } = props;
    return data.map((item) => {
      const { symbolAndTime } = getSymbolAndTimeAndPair(item);
      return {
        ...symbolAndTime,
        'tx hash': item.txHash,
        side: item.side,
        size: item.size,
        price: item.price,
        'trade value': item.balance,
        fee: item.tradeFee,
        type: item.typeString,
      };
    });
  }
  if (props.type === HISTORY_TYPE.ORDERS) {
    const { data } = props;
    return data.map((item) => {
      const { symbolAndTime } = getSymbolAndTimeAndPair({
        ...item,
        timestamp: item.typeString === 'cancelled' ? item.cancelTimestamp : item.placeTimestamp,
      });
      return {
        'tx hash': item.placeTxHash,
        ...symbolAndTime,
        side: item.side,
        size: item.size,
        'filled size': item.takenSize,
        'order price': item.orderPrice,
        'fee rebate': item.feeRebate,
        'trade value': item.takenBalance,
        status: item.typeString,
      };
    });
  }
  if (props.type === HISTORY_TYPE.LIQUIDITY) {
    const { data } = props;
    return data.flatMap((item) => {
      if (item.instrumentAddress && item.expiry !== undefined) {
        const { symbolAndTime } = getSymbolAndTimeAndPair(item);
        return [
          {
            'tx hash': item.txHash,
            ...symbolAndTime,
            type: item.typeString,
            'fair price': item.fairPrice,
            'upper price': item.upperPrice,
            'lower price': item.lowerPrice,
            amount: item.amount,
            'fees earned': item.feeEarned,
          },
        ];
      }
      return [];
    });
  }
  if (props.type === HISTORY_TYPE.FUNDING) {
    const { data } = props;
    return data.flatMap((item) => {
      if (item.instrumentAddress && item.expiry !== undefined) {
        const { symbolAndTime } = getSymbolAndTimeAndPair(item);
        return [
          {
            'tx hash': item.txHash,
            ...symbolAndTime,
            type: item.typeString,
            value: item.value,
          },
        ];
      }
      return [];
    });
  }
  if (props.type === HISTORY_TYPE.TRANSFERS) {
    const { data } = props;
    const result = data.flatMap((item) => {
      const { symbolAndTime } = getSymbolAndTimeAndPair(item);
      return [
        {
          'tx hash': item.txHash,
          ...symbolAndTime,
          direction: item.typeString !== 'scatter' ? 'Transfer In' : 'Transfer Out',
          amount: item.value,
        },
      ];
    });

    return result;
  }
  if (props.type === HISTORY_TYPE.ACCOUNT) {
    const { data, marginTokenMap } = props;
    console.log(`account history data`, data);

    return data.map((item) => {
      const tokenInfo = marginTokenMap?.[item.tokenAddress];
      return {
        time: item.timestamp ? new Date(item.timestamp * 1000).toLocaleString('en-GB') : item.timestamp,
        'tx hash': item.txHash,
        account: tokenInfo?.symbol || item.tokenInfo?.symbol,
        type: item.typeString,
        amount: item.value,
      };
    });
  }
  return [];
};
